/*
  dopamiles-pdp-variant-sync.js
  Phase 06 extraction (260511) — was inline in sections/dopamiles-product-hero.liquid:208-503.

  Three responsibilities consolidated into one boot pass per PDP section:
    1. Variant sync — radio/select change → patch hidden input, ATC label/disabled,
       price block, stock indicator, SKU, sticky-mobile-ATC. Dispatches
       `bnc:variant-media-change` so dopamiles-pdp.js can swap the gallery image.
    2. ATC fallback toast — if Dawn's product-form.js stalls in "loading" state
       (no cart-drawer-open listener fires), force-reset the button after 4s and
       toast a link to /cart. Cancelled by Dawn's PUB_SUB_EVENTS.cartUpdate.
    3. Globo coexistence (phase 04 carry-over) — Dawn's <variant-selects> is hidden
       by CSS rule [data-dawn-vs] variant-selects { display:none }. If Globo never
       injects within 5s, reveal Dawn so PDP stays usable.

  Boot strategy:
    - DOMContentLoaded → init all `[data-bnc-variants-json]` islands present
    - shopify:section:load (Theme Editor / Section Rendering API) → init the
      reloaded section if it contains our island
*/
(function () {
  'use strict';

  // ── Money formatting ─────────────────────────────────────────────────────
  // Backlog item #24: replace the prior hardcoded "$X / $X.YY" formatter with
  // a proper money_format parser so non-USD shops render correctly.
  //
  // Strategy: prefer window.Shopify.formatMoney if present (loaded by shops
  // using vintage shopify_common.js). Otherwise port the same algorithm
  // inline — pod-tee-theme doesn't register shopify_common.js, so the inline
  // port is the actual runtime path. window.shopMoneyFormat is set from
  // {{ shop.money_format }} in layout/theme.liquid.
  function formatWithDelimiters(cents, precision, thousands, decimal) {
    if (thousands == null) thousands = ',';
    if (decimal == null)   decimal   = '.';
    if (isNaN(cents) || cents == null) return '0';
    var num   = (cents / 100).toFixed(precision);
    var parts = num.split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    var fraction = parts[1] ? (decimal + parts[1]) : '';
    return dollars + fraction;
  }

  function fmtMoney(cents) {
    var format = window.shopMoneyFormat || '${{amount}}';

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, format);
    }

    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var match = format.match(placeholderRegex);
    if (!match) return format; // money_format has no placeholder — return as-is
    var value;
    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2); break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ','); break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' '); break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'"); break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return format.replace(placeholderRegex, value);
  }

  // Expose the shared money formatter on window so other scripts
  // (dopamiles-pdp.js bundle/FBT totals) can share the same money_format
  // parsing instead of duplicating the algorithm. Backlog item #15.
  if (!window.bncFmtMoney) {
    window.bncFmtMoney = fmtMoney;
  }

  // ── i18n strings ─────────────────────────────────────────────────────────
  // window.variantStrings exposed in layout/theme.liquid:387-392.
  function vStr(key, fallback) {
    var s = window.variantStrings && window.variantStrings[key];
    return s || fallback;
  }

  // ── Per-section boot ─────────────────────────────────────────────────────
  function initSection(section) {
    if (!section || section.dataset.bncVariantSyncInit === '1') return;
    section.dataset.bncVariantSyncInit = '1';

    var jsonEl = section.querySelector('[data-bnc-variants-json]');
    if (!jsonEl) return;

    var variants = [];
    try { variants = JSON.parse(jsonEl.textContent) || []; } catch (e) { return; }
    if (!variants.length) return;

    // Re-query DOM each call. Dawn's product-info.js refetches the section
    // after variant change and replaces <variant-selects> + form children,
    // so cached refs from initial run go stale (bug #5).
    function getPicker() { return section.querySelector('variant-selects'); }
    function getHidden() { return section.querySelector('[data-bnc-variant-id]'); }

    // Patches: hidden input, ATC label/disabled, price block, stock
    // indicator, SKU, sticky-mobile-ATC, gallery custom event.
    function syncVariant() {
      var picker = getPicker();
      var hidden = getHidden();
      if (!picker || !hidden) return { matched: false, mediaId: null };

      // Read selected option values. Default picker uses radio buttons (one
      // input[type="radio"]:checked per option group). When a merchant
      // configures Shopify's Variant Picker to use the dropdown style, the
      // picker emits <select> elements instead — no radios will ever be
      // checked, so collect <select>.value as the fallback. Belt-and-
      // suspenders: combine both selectors so a future hybrid picker (some
      // options as radios, others as selects) still resolves cleanly.
      var checkedRadios = picker.querySelectorAll('input[type="radio"]:checked');
      var selected = Array.prototype.map.call(checkedRadios, function (r) { return r.value; });
      if (selected.length === 0) {
        var selectEls = picker.querySelectorAll('select');
        selected = Array.prototype.map.call(selectEls, function (s) { return s.value; })
          .filter(function (v) { return v !== ''; });
      }
      var match = variants.find(function (v) {
        return v.options.length === selected.length
          && v.options.every(function (opt, i) { return opt === selected[i]; });
      });

      var btn        = section.querySelector('[data-bnc-main-atc]');
      var btnText    = btn && btn.querySelector('.bnc-btn-text');
      var priceEl    = section.querySelector('.bnc-price');
      var wasEl      = section.querySelector('.bnc-price-was');
      var tagEl      = section.querySelector('.bnc-price-tag');
      var stockBox   = section.querySelector('[data-bnc-stock]');
      var stockText  = section.querySelector('[data-bnc-stock-text]');
      var skuEl      = section.querySelector('[data-bnc-sku]');
      // Sticky-ATC lives outside this section (separate section via product.json).
      var stickyBtn     = document.querySelector('[data-sticky-atc-btn]');
      var stickyLabel   = document.querySelector('[data-sticky-atc-label]');
      var stickyPrice   = document.querySelector('[data-sticky-atc-price]');
      var stickyCompare = document.querySelector('[data-sticky-atc-compare]');

      function setStickyState(disabled, label) {
        if (stickyBtn) {
          stickyBtn.disabled = !!disabled;
          stickyBtn.setAttribute('aria-label', label);
        }
        if (stickyLabel) stickyLabel.textContent = label;
      }

      var UNAVAILABLE = vStr('unavailable', 'Unavailable');
      var ADD_TO_CART = vStr('addToCart', 'Add to cart');
      var SOLD_OUT    = vStr('soldOut', 'Sold out');

      if (!match) {
        hidden.value = '';
        if (btn) {
          btn.disabled = true;
          if (btnText) btnText.textContent = UNAVAILABLE;
        }
        if (priceEl) priceEl.textContent = '';
        if (wasEl) wasEl.style.display = 'none';
        if (tagEl) tagEl.style.display = 'none';
        if (stockBox && stockText) {
          stockBox.classList.remove('low');
          stockText.textContent = UNAVAILABLE;
        }
        setStickyState(true, UNAVAILABLE);
        if (stickyPrice) stickyPrice.textContent = '';
        if (stickyCompare) {
          stickyCompare.setAttribute('hidden', '');
          stickyCompare.textContent = '';
        }
        return { matched: false, mediaId: null };
      }

      hidden.value = match.id;

      // Main ATC button
      if (btn) {
        btn.disabled = !match.available;
        if (btnText) btnText.textContent = match.available ? ADD_TO_CART : SOLD_OUT;
      }

      // Price block
      if (priceEl) priceEl.textContent = fmtMoney(match.price);
      var hasCompare = match.compare_at_price && match.compare_at_price > match.price;
      if (wasEl) {
        wasEl.style.display = hasCompare ? '' : 'none';
        if (hasCompare) wasEl.textContent = fmtMoney(match.compare_at_price);
      }
      if (tagEl) {
        tagEl.style.display = hasCompare ? '' : 'none';
        if (hasCompare) {
          tagEl.textContent = 'Save ' + fmtMoney(match.compare_at_price - match.price);
        }
      }

      // Stock indicator. Templates come from section.settings (Phase 09)
      // via data-attrs on stockBox; JS substitutes [qty] placeholder.
      if (stockBox && stockText) {
        var qty = match.inventory_quantity;
        var managed = match.inventory_management === 'shopify';
        var isLow = managed && qty > 0 && qty <= 5;
        stockBox.classList.toggle('low', isLow);
        if (!match.available) {
          stockText.textContent = SOLD_OUT;
        } else if (isLow) {
          var lowTpl = stockBox.getAttribute('data-bnc-stock-low-template')
            || 'Only [qty] left. Ships in 7 days.';
          // Global regex — matches Liquid's `replace` semantics so merchants
          // can use `[qty]` more than once in the template (review C1).
          // Placeholder uses `[qty]` (not Liquid `{{ }}`) because Shopify's
          // template JSON parser interprets `{{ ... }}` in setting defaults as
          // dynamic-source refs and rejects unknown sources at theme push.
          stockText.textContent = lowTpl.replace(/\[qty\]/g, qty);
        } else {
          var inTpl = stockBox.getAttribute('data-bnc-stock-in-template')
            || 'In stock · Ships in 7 days.';
          stockText.textContent = inTpl;
        }
      }

      if (skuEl) skuEl.textContent = match.sku || '';

      // Sticky-mobile-ATC
      if (stickyPrice) stickyPrice.textContent = fmtMoney(match.price);
      if (stickyCompare) {
        if (hasCompare) {
          stickyCompare.textContent = fmtMoney(match.compare_at_price);
          stickyCompare.removeAttribute('hidden');
        } else {
          stickyCompare.setAttribute('hidden', '');
          stickyCompare.textContent = '';
        }
      }
      setStickyState(!match.available, match.available ? ADD_TO_CART : SOLD_OUT);

      // Bug #4: tell dopamiles-pdp.js gallery to swap media.
      var mediaId = match.featured_media && match.featured_media.id;
      var imgSrc  = match.featured_image && (match.featured_image.src || match.featured_image);
      document.dispatchEvent(new CustomEvent('bnc:variant-media-change', {
        detail: { mediaId: mediaId, imgSrc: imgSrc, variant: match }
      }));

      return { matched: true, mediaId: mediaId };
    }

    // Race-condition fix: expose syncVariant so dopamiles-pdp.js initGallery()
    // can re-fire it AFTER its bnc:variant-media-change listener is wired.
    window.bncSyncVariant = syncVariant;

    // Event delegation on stable section wrapper — re-rendered radios still work.
    section.addEventListener('change', function (e) {
      var t = e && e.target;
      if (!t) return;
      if (t.matches && t.matches('input[type="radio"], select')) {
        syncVariant();
      }
    });
    syncVariant();

    // Belt-and-braces: subscribe to Dawn's variantChange pubsub for URL deep-links etc.
    function wireVariantPubsub() {
      try {
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          subscribe(PUB_SUB_EVENTS.variantChange, function () { syncVariant(); });
          subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, function () { syncVariant(); });
        }
      } catch (e) { /* fallback: section change-delegation still works */ }
    }
    wireVariantPubsub();

    // Bug #3: ATC stuck on "…" fallback toast.
    // Listener is delegated at the section element (not the form) so it
    // survives form rebinds — Dawn's variant-radios web component can
    // replace the form DOM via fetch(...), and a directly-bound listener
    // on the old form node would be lost after every variant change. The
    // section element is stable across variant changes.
    var pendingTimer = null;
    var lastSubmitToken = 0;

    section.addEventListener('submit', function (e) {
      var formEl = e.target;
      if (!formEl || typeof formEl.matches !== 'function') return;
      if (!formEl.matches('form[data-bnc-product-form]')) return;
      var myToken = ++lastSubmitToken;
      clearTimeout(pendingTimer);
      pendingTimer = setTimeout(function () {
        if (myToken !== lastSubmitToken) return; // superseded
        var atc = section.querySelector('[data-bnc-main-atc]');
        if (!atc) return;
        var spinner = atc.querySelector('.loading__spinner, .loading-overlay__spinner');
        var stillLoading = atc.classList.contains('loading')
          || (spinner && getComputedStyle(spinner).display !== 'none');
        if (stillLoading) {
          atc.classList.remove('loading');
          if (spinner) spinner.style.display = 'none';
          showAtcToast('Added — view cart →', '/cart');
        }
      }, 4000);
    });

    // Cancel fallback if Dawn's cartUpdate pubsub fires (real success path).
    function wireCartPubsub() {
      try {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
        subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
          lastSubmitToken++;
          clearTimeout(pendingTimer);
        });
        subscribe(PUB_SUB_EVENTS.cartError, function () {
          lastSubmitToken++;
          clearTimeout(pendingTimer);
        });
      } catch (e) { /* toast fallback still works */ }
    }
    wireCartPubsub();

    function showAtcToast(text, link) {
      var existing = document.querySelector('.bnc-atc-toast');
      if (existing) existing.remove();
      var t = document.createElement('a');
      t.href = link;
      t.className = 'bnc-atc-toast';
      t.textContent = text;
      t.style.cssText = [
        'position:fixed', 'bottom:80px', 'left:50%', 'transform:translateX(-50%)',
        'background:var(--bnc-ink, #3B352C)', 'color:#fff',
        'padding:12px 20px', 'border-radius:999px', 'text-decoration:none',
        'z-index:1000', 'box-shadow:0 4px 16px rgba(0,0,0,.2)',
        'font-size:14px', 'font-weight:500'
      ].join(';');
      document.body.appendChild(t);
      setTimeout(function () { if (t.parentNode) t.remove(); }, 3500);
    }

    // Phase 04 — Globo coexistence via MutationObserver.
    // Globo present → leave Dawn hidden. Absent + injects within 5s → leave hidden.
    // Absent + never injects → reveal Dawn so PDP stays usable.
    var GLOBO_SELECTOR = '.globo-swatch-product-detail, [class*="globo-color-swatch"]';
    var dawnEl = section.querySelector('[data-dawn-vs]');

    function revealDawn() {
      if (dawnEl) dawnEl.setAttribute('data-dawn-revealed', '');
    }
    function globoPresent() {
      return !!section.querySelector(GLOBO_SELECTOR);
    }

    // Globo default-selection override: Globo's own default-selection logic
    // ignores Shopify variant order, so the highlighted swatch on PDP load
    // does NOT match product.selected_or_first_available_variant.options[0].
    // Server emits the preferred color in `[data-bnc-variants-json]
    // data-bnc-preferred-color`. We find Globo's matching swatch and click it
    // once per init. Multi-selector approach to handle Globo's unknown DOM.
    var preferredColor = jsonEl.dataset.bncPreferredColor || '';

    function normalizeColor(s) {
      return String(s || '').toLowerCase().replace(/[\s_-]/g, '');
    }

    function alignGloboToPreferredColor() {
      if (section.dataset.bncGloboAligned === '1') return;
      if (!preferredColor) { section.dataset.bncGloboAligned = '1'; return; }
      if (!globoPresent()) return;

      var target = normalizeColor(preferredColor);

      // Globo Color Swatches v2.x DOM (verified in QA 2026-05-18):
      //   .globo-swatch-product-detail
      //     .globo-swatch-list
      //       .swatch--gl > ul.value > li.select-option[data-value="Navy"]
      //         <input type="radio" name="globo-option-0" value="Navy" checked>
      //         <label class="swatch-anchor">...</label>
      // Selected state lives on the <input>'s `checked` property.
      // Clickable target is the <label> (radio inputs are visually hidden).
      var swatchItems = section.querySelectorAll(
        '.globo-swatch-product-detail li[data-value], ' +
        '.globo-swatch-product-detail [data-value]:not(label):not(input), ' +
        '[class*="globo-color-swatch"] li[data-value]'
      );
      // Fallback: query radios directly (their `value` holds the color name).
      var radios = section.querySelectorAll(
        '.globo-swatch-product-detail input[type="radio"], ' +
        '[class*="globo-color-swatch"] input[type="radio"]'
      );
      if (!swatchItems.length && !radios.length) return; // children not painted yet

      // Try swatch items first (data-value is most reliable).
      var matchedInput = null;
      var clickTarget = null;

      for (var i = 0; i < swatchItems.length; i++) {
        var li = swatchItems[i];
        var candidates = [
          li.getAttribute('data-value'),
          li.getAttribute('data-color'),
          li.getAttribute('data-globo-value'),
          li.getAttribute('aria-label'),
          li.getAttribute('title'),
        ];
        for (var j = 0; j < candidates.length; j++) {
          if (candidates[j] && normalizeColor(candidates[j]) === target) {
            matchedInput = li.querySelector('input[type="radio"]');
            clickTarget = li.querySelector('label') || matchedInput || li;
            break;
          }
        }
        if (matchedInput || clickTarget) break;
      }

      // Fallback: scan radios by their `value` attribute.
      if (!clickTarget) {
        for (var k = 0; k < radios.length; k++) {
          var r = radios[k];
          var v = r.getAttribute('value') || r.getAttribute('aria-label');
          if (v && normalizeColor(v) === target) {
            matchedInput = r;
            // Prefer clicking the associated <label> over the (hidden) radio.
            var id = r.getAttribute('id');
            var lbl = id ? section.querySelector('label[for="' + id + '"]') : null;
            clickTarget = lbl || r;
            break;
          }
        }
      }

      if (!clickTarget) {
        // No swatch matched preferred color — surface a console warn for QA
        // visibility but mark aligned to prevent retry loop.
        section.dataset.bncGloboAligned = '1';
        if (window.console && console.warn) {
          console.warn('[bnc] Globo swatch not found for preferred color:', preferredColor);
        }
        return;
      }

      // Already selected? Check the underlying radio's `checked` property.
      var alreadySelected = !!(matchedInput && matchedInput.checked);

      section.dataset.bncGloboAligned = '1';
      if (!alreadySelected) {
        try { clickTarget.click(); } catch (e) {}
      }
    }

    // Try once in case Globo is already injected at init time.
    alignGloboToPreferredColor();

    // Observer must run even when globoPresent() is already true — the container
    // can render before its swatch children paint, in which case the first
    // alignGloboToPreferredColor() call exits early (no swatches found) and
    // we need mutations to retry. Disconnect is keyed on alignment success,
    // not container presence.
    if (section.dataset.bncGloboAligned !== '1') {
      var globoObserver = new MutationObserver(function () {
        alignGloboToPreferredColor();
        if (section.dataset.bncGloboAligned === '1') globoObserver.disconnect();
      });
      globoObserver.observe(section, { childList: true, subtree: true });
      setTimeout(function () {
        globoObserver.disconnect();
        if (!globoPresent()) revealDawn();
      }, 5000);
    }
  }

  // ── Discovery + boot ─────────────────────────────────────────────────────
  // The Shopify section wrapper is the closest .shopify-section ancestor of
  // our [data-bnc-variants-json] data island. That wrapper is what the old
  // inline script obtained via getElementById('shopify-section-{id}').
  function findSection(node) {
    return node && (node.closest('.shopify-section') || node.parentElement);
  }

  function bootAll(root) {
    var scope = root || document;
    var islands = scope.querySelectorAll('[data-bnc-variants-json]');
    Array.prototype.forEach.call(islands, function (island) {
      initSection(findSection(island));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { bootAll(); });
  } else {
    bootAll();
  }

  // Theme Editor / Section Rendering API: re-init the freshly-loaded section.
  document.addEventListener('shopify:section:load', function (e) {
    bootAll(e.target);
  });
})();
